import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, ClipboardList, Users, ShieldCheck, Check, PhoneCall, FileCheck2, BadgeCheck, Handshake } from "lucide-react";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";
import { BookCallDialog } from "../components/BookCallDialog";
import { MarketingCapture } from "../components/MarketingCapture";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PLANS, WHY_MEMBER, EVERY_MEMBERSHIP } from "../lib/membership";

const IMG = "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?auto=format&fit=crop&w=1200&q=80";

const steps = [
  { icon: Building2, title: "Brief us", text: "Tell us the role, the must-haves, the salary and the start date. That's your job done." },
  { icon: ClipboardList, title: "We go to market", text: "We run targeted campaigns on LinkedIn and Seek, search our talent pool and headhunt directly — you never sift a CV pile." },
  { icon: ShieldCheck, title: "We vet everyone", text: "Credentials, references and Australian work rights verified by our recruiters before anyone reaches you." },
  { icon: Users, title: "You hire from a shortlist", text: "A hand-picked shortlist for your role — not a database to browse. Quality over quantity, always." },
];

export default function EmployerLanding() {
  const [callOpen, setCallOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#fcfbf8]">
      <PublicHeader />
      <section className="pt-16 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center py-20">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#7C8514] font-medium">For employers</p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black uppercase text-[#0d2f4d] tracking-tighter leading-[0.95] mt-5">
              Stop sifting.<br /><span className="font-accent font-medium normal-case italic text-[#7C8514]">Start hiring.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#51637a] max-w-lg leading-relaxed">
              You don't post a job here — you give us the brief. We advertise on LinkedIn and Seek,
              search our talent pool, headhunt, and vet everyone ourselves. You get a shortlist of
              people who can actually do the job — vetted, work-rights-checked and ready to start,
              in every major Australian city.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/signup/employer" data-testid="employer-landing-signup"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0d2f4d] text-white font-medium px-8 py-4 hover:bg-[#1c4d78] transition-colors">
                Submit a role <ArrowRight size={18} />
              </Link>
              <Link to="/signin" data-testid="employer-landing-signin"
                className="inline-flex items-center justify-center rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium px-8 py-4 hover:bg-[#0d2f4d] hover:text-white transition-colors">
                Sign in
              </Link>
            </div>
          </div>
          <img src={IMG} alt="Team collaborating" className="rounded-lg object-cover w-full h-[420px]" />
        </div>
      </section>

      <section className="bg-white border-y border-[#e3e0d3]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight">How working with us works</h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className="relative">
                <span className="font-heading text-5xl font-extrabold text-[#f5f3ee] absolute -top-4 right-0">{`0${i + 1}`}</span>
                <Icon className="text-[#7C8514]" size={26} />
                <h3 className="font-heading font-bold text-[#0d2f4d] text-lg mt-4">{title}</h3>
                <p className="text-sm text-[#51637a] mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight">Roles and support we cover</h2>
        <div className="mt-10 grid sm:grid-cols-3 gap-6">
          {[["Permanent placements", "Full-time hires across trades, hospitality, healthcare, professional services and more."],
            ["Contract & seasonal", "Short-term and seasonal staffing with work rights verified up front."],
            ["Relocation-ready talent", "Candidates from New Zealand and the UK, prepped and ready to make the move to Australia."]].map(([t, d]) => (
            <div key={t} className="ditch-card p-7">
              <h3 className="font-heading font-bold text-[#0d2f4d] text-lg">{t}</h3>
              <p className="text-sm text-[#51637a] mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <div className="mt-20" data-testid="shortlist-detail-section">
          <p className="font-accent text-lg text-[#7C8514]">No CV piles here</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">What lands in your shortlist</h2>
          <p className="text-[#51637a] mt-3 max-w-2xl leading-relaxed">
            Every candidate starts with our CV creator — their CV is converted into a complete, structured profile,
            then a recruiter vets it before it goes anywhere near you. By the time a candidate reaches your shortlist, the homework is done.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FileCheck2, title: "A complete, structured profile", text: "Full work history, skills, qualifications and licences — extracted from their CV, reviewed by the candidate and checked by us. No deciphering seven formats of CV." },
              { icon: BadgeCheck, title: "Work rights, verified", text: "Citizens, permanent residents or visa holders — every candidate's right to work in Australia is confirmed before they're presented. No surprises at offer stage." },
              { icon: ShieldCheck, title: "References & credentials checked", text: "We speak to referees and verify qualifications and police checks where required — before the shortlist, not after you've fallen for someone on paper." },
              { icon: Handshake, title: "Briefed and genuinely interested", text: "Every shortlisted candidate has spoken with a recruiter about your role, your company and the salary. If they're on your list, they want the job." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="ditch-card p-7">
                <Icon size={24} className="text-[#7C8514]" />
                <h3 className="font-heading font-bold text-[#0d2f4d] text-lg mt-4">{title}</h3>
                <p className="text-sm text-[#51637a] mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20" data-testid="membership-section">
          <p className="font-accent text-lg text-[#7C8514]">The Ditch Careers Recruitment Membership</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Your Recruitment Team. Without the Recruitment Overheads.</h2>
          <div className="text-sm md:text-base text-[#51637a] mt-5 max-w-2xl leading-relaxed space-y-3">
            <p><span className="font-medium text-[#0d2f4d]">Recruitment shouldn't be reactive.</span> The best businesses don't wait until someone resigns to think about hiring. They build talent pipelines, plan ahead, and partner with experts who understand their business. That's exactly what we do.</p>
            <p>The Ditch Careers Recruitment Membership provides your business with ongoing access to an experienced recruitment team for one predictable monthly investment. No unexpected recruitment invoices. No scrambling to fill vacancies. Just a trusted recruitment partner invested in your long-term success.</p>
          </div>

          <a href="/membership-pack.pdf" download="The_Ditch_Careers_Employer_Membership_Pack.pdf" data-testid="landing-download-pack"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium text-sm px-6 py-3 hover:bg-[#0d2f4d] hover:text-white transition-colors">
            Download the Membership Pack (PDF)
          </a>

          <div className="ditch-card p-7 mt-10" data-testid="why-member-section">
            <h3 className="font-heading font-bold text-[#0d2f4d] text-lg">Why become a member?</h3>
            <p className="text-sm text-[#51637a] mt-2">Unlike traditional agencies that charge a large fee every time you hire, our membership gives you continuous recruitment support throughout the year. As a member you'll receive:</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {WHY_MEMBER.map((w) => (
                <p key={w} className="flex items-start gap-2 text-sm text-[#122c47]"><Check size={15} className="text-[#7C8514] mt-0.5 shrink-0" /> {w}</p>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-10" data-testid="pricing-cards">
            {PLANS.map((p) => (
              <div key={p.key} className={`ditch-card p-7 flex flex-col ${p.popular ? "border-[#C1CC24] bg-[#C1CC24]/5" : ""}`} data-testid={`landing-plan-${p.key}`}>
                {p.popular && <span className="self-start rounded-full bg-[#C1CC24] text-[#0d2f4d] text-xs font-semibold px-3 py-1 mb-3">Most popular</span>}
                <h3 className="font-heading font-bold text-[#0d2f4d] text-xl">{p.name} <span className="font-accent font-medium">Membership</span></h3>
                <p className="mt-3"><span className="font-heading text-3xl font-extrabold text-[#0d2f4d]">{p.price}</span><span className="text-sm text-[#51637a]"> + GST /month</span></p>
                <p className="text-sm text-[#51637a] mt-3 leading-relaxed">{p.tagline}</p>
                <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#51637a] mt-5">{p.lead}</p>
                <ul className="mt-3 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#122c47]">
                      <Check size={15} className="text-[#7C8514] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup/employer" data-testid={`landing-become-member-${p.key}`}
                  className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm py-3.5 transition-colors ${
                    p.popular ? "bg-[#C1CC24] text-[#0d2f4d] hover:bg-[#aeb81f]" : "bg-[#0d2f4d] text-white hover:bg-[#1c4d78]"}`}>
                  Become a member
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-[#f5f3ee] border border-[#e3e0d3] rounded-lg p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-testid="not-sure-section">
            <div>
              <h3 className="font-heading font-bold text-[#0d2f4d] text-lg">Unsure about signing up yet?</h3>
              <p className="text-sm text-[#51637a] mt-1.5">Not sure which option's right for you? Book a call and we can work out what's right for you — no commitment.</p>
            </div>
            <button onClick={() => setCallOpen(true)} data-testid="not-sure-book-call"
              className="shrink-0 inline-flex items-center gap-2 rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium text-sm px-6 py-3 hover:bg-[#0d2f4d] hover:text-white transition-colors">
              <PhoneCall size={15} /> Book a call
            </button>
          </div>

          <div className="ditch-card p-7 mt-8" data-testid="every-membership-section">
            <h3 className="font-heading font-bold text-[#0d2f4d] text-lg">Every membership includes</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {EVERY_MEMBERSHIP.map((e) => (
                <span key={e} className="rounded-full bg-[#f5f3ee] border border-[#e3e0d3] text-xs text-[#0d2f4d] px-3.5 py-1.5">{e}</span>
              ))}
            </div>
            <p className="text-sm text-[#51637a] mt-5 leading-relaxed">
              <span className="font-medium text-[#0d2f4d]">Additional placements:</span> if your business exceeds the successful placements included in your membership,
              additional placements are available at an exclusive member rate — always with priority service and preferred pricing over our standard recruitment fees.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div className="ditch-card p-7" data-testid="ditch-difference-section">
              <h3 className="font-heading font-bold text-[#0d2f4d] text-lg">The Ditch difference</h3>
              <p className="text-sm text-[#51637a] mt-3 leading-relaxed">
                Most recruitment agencies work transaction by transaction. <span className="font-medium text-[#0d2f4d]">We work relationship by relationship.</span> We
                take the time to understand your business, your culture, your leadership team and your future hiring plans — so when
                opportunities arise, we're already working on your behalf. We're not simply filling vacancies. We're helping businesses build exceptional teams.
              </p>
            </div>
            <div className="ditch-card p-7" data-testid="commitment-section">
              <h3 className="font-heading font-bold text-[#0d2f4d] text-lg">Our commitment</h3>
              <p className="text-sm text-[#51637a] mt-3 leading-relaxed">
                Every candidate we represent has been carefully screened, interviewed and matched — not only for skills and experience,
                but for cultural alignment and long-term potential. When you partner with The Ditch Careers, you're gaining more than
                a recruiter. <span className="font-medium text-[#0d2f4d]">You're gaining an extension of your business.</span>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-20 grid lg:grid-cols-2 gap-12" data-testid="employer-faq-section">
          <div>
            <p className="font-accent text-lg text-[#7C8514]">Before you ask</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Questions employers ask us</h2>
            <p className="text-[#51637a] mt-3 max-w-md leading-relaxed">
              Still unsure about something? Book a call — a recruiter will talk it through, no pitch, no pressure.
            </p>
            <button onClick={() => setCallOpen(true)} data-testid="faq-book-call"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium text-sm px-6 py-3 hover:bg-[#0d2f4d] hover:text-white transition-colors">
              <PhoneCall size={15} /> Book a call
            </button>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {[
              ["Do I need a membership to hire through you?", "No — you can engage us for a one-off placement with fees agreed up front. Membership is simply the best value if you hire more than once or twice a year: predictable monthly cost, priority service and placements included."],
              ["How fast will I see candidates?", "We're at market within 24–48 hours of taking your brief — advertising on LinkedIn and Seek, searching our talent pool and headhunting directly. Shortlists typically follow within days, depending on how specialised the role is."],
              ["What if the hire doesn't work out?", "Every membership includes a 3-month replacement guarantee. If a placement leaves within the first three months, we replace them — no additional placement fee, no re-brief from scratch."],
              ["Where do your candidates come from?", "Three places: our own advertising campaigns, our vetted talent pool, and direct headhunting. That includes local Australian talent plus relocation-ready candidates from New Zealand and the UK — all with verified Australian work rights."],
              ["Can I cancel my membership?", "Yes — memberships are billed monthly through Stripe and you can cancel anytime. No lock-in contracts, no exit fees."],
              ["Do you handle visa and relocation logistics?", "We verify every candidate's right to work in Australia before they reach your shortlist, and we guide relocating candidates through timing and the move — so by the time you make an offer, the pathway is already mapped."],
            ].map(([q, a], i) => (
              <AccordionItem key={q} value={`efaq-${i}`} data-testid={`employer-faq-${i}`}>
                <AccordionTrigger className="text-left font-heading font-semibold text-[#0d2f4d] hover:text-[#7C8514] hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-sm text-[#51637a] leading-relaxed">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-16 bg-[#0d2f4d] rounded-lg grain relative overflow-hidden p-10 sm:p-14" data-testid="employer-final-cta">
          <h2 className="relative text-2xl sm:text-3xl font-bold text-white tracking-tight max-w-lg">Let's build your team together.</h2>
          <div className="flex flex-col sm:flex-row gap-4 mt-7">
            <Link to="/signup/employer" data-testid="employer-landing-cta-bottom"
              className="relative inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-medium px-8 py-4 hover:bg-[#aeb81f] transition-colors">
              Get started <ArrowRight size={18} />
            </Link>
            <button onClick={() => setCallOpen(true)} data-testid="final-cta-book-call"
              className="relative inline-flex items-center justify-center gap-2 rounded-full border border-white/50 text-white font-medium px-8 py-4 hover:bg-white hover:text-[#0d2f4d] transition-colors">
              <PhoneCall size={16} /> Not sure yet? Book a call
            </button>
          </div>
          <p className="relative text-xs text-white/60 mt-5">Secure checkout by Stripe. Prices in AUD, billed monthly. Cancel anytime.</p>
        </div>
      </section>
      <MarketingCapture source="employer_landing" />
      <BookCallDialog open={callOpen} onOpenChange={setCallOpen} />
      <SiteFooter />
    </div>
  );
}
